import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://devwellnessapi.jyada.in/api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!token || !email) {
            setError('Invalid or missing reset link. Please request a new one.');
        }
    }, [token, email]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API_BASE}/auth/reset-password`, { token, email, password });
            setDone(true);
        } catch (err) {
            const msg = err.response?.data?.error || 'Reset failed. The link may have expired.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ---- Styles ----
    const styles = {
        page: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        },
        card: {
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
            padding: '44px 40px',
            width: '100%',
            maxWidth: 440,
        },
        logo: {
            textAlign: 'center',
            marginBottom: 28,
        },
        logoText: {
            fontSize: 26,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #4CAF50, #2196F3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        title: {
            fontSize: 22,
            fontWeight: 700,
            color: '#1a1a2e',
            marginBottom: 6,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: 14,
            color: '#666',
            textAlign: 'center',
            marginBottom: 28,
        },
        label: {
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#444',
            marginBottom: 6,
        },
        inputWrapper: {
            position: 'relative',
            marginBottom: 18,
        },
        input: {
            width: '100%',
            padding: '12px 42px 12px 14px',
            border: '1.5px solid #e0e0e0',
            borderRadius: 10,
            fontSize: 15,
            color: '#222',
            background: '#f9f9fb',
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.2s',
        },
        eyeBtn: {
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#888',
            fontSize: 18,
            padding: 0,
        },
        btn: {
            width: '100%',
            padding: '13px',
            background: 'linear-gradient(135deg, #4CAF50, #2196F3)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: 0.5,
            marginTop: 6,
            opacity: loading ? 0.7 : 1,
        },
        errorBox: {
            background: '#fff0f0',
            border: '1px solid #ffcdd2',
            color: '#c62828',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 16,
        },
        successBox: {
            textAlign: 'center',
        },
        successIcon: {
            fontSize: 56,
            marginBottom: 12,
        },
        successTitle: {
            fontSize: 20,
            fontWeight: 700,
            color: '#2e7d32',
            marginBottom: 8,
        },
        successText: {
            fontSize: 14,
            color: '#555',
            marginBottom: 24,
            lineHeight: 1.6,
        },
        backBtn: {
            display: 'inline-block',
            padding: '11px 28px',
            background: 'linear-gradient(135deg, #4CAF50, #2196F3)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14,
        },
    };

    if (done) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.successBox}>
                        <div style={styles.successIcon}>✅</div>
                        <div style={styles.successTitle}>Password Reset Successful!</div>
                        <p style={styles.successText}>
                            Your password has been updated. You can now log in to the Wellness app with your new password.
                        </p>
                        <button style={styles.backBtn} onClick={() => navigate('/')}>
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.logo}>
                    <span style={styles.logoText}>🏃 DevWellness</span>
                </div>
                <div style={styles.title}>Set a new password</div>
                <p style={styles.subtitle}>Enter your new password below. It must be at least 6 characters.</p>

                {error && <div style={styles.errorBox}>{error}</div>}

                {!error || (token && email) ? (
                    <form onSubmit={handleSubmit}>
                        <label style={styles.label}>New Password</label>
                        <div style={styles.inputWrapper}>
                            <input
                                style={styles.input}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                style={styles.eyeBtn}
                                onClick={() => setShowPassword((v) => !v)}
                                tabIndex={-1}
                            >
                                {showPassword ? '🙈' : '👁️'}
                            </button>
                        </div>

                        <label style={styles.label}>Confirm Password</label>
                        <div style={styles.inputWrapper}>
                            <input
                                style={styles.input}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Re-enter new password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>

                        <button style={styles.btn} type="submit" disabled={loading || !!error}>
                            {loading ? 'Resetting…' : 'Reset Password'}
                        </button>
                    </form>
                ) : null}
            </div>
        </div>
    );
};

export default ResetPassword;
